import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useAuth } from "auth";
import issuer_services from "services/issuer_services";
import {
  treasuryIdSelector,
  networkTypeSelector,
} from "store/userSliceHelpers";
import { parseApiError } from "utils/errors/parseApiError";
import { toTerminalRecord } from "pages/issuer-page/useIssuerTerminals";
import { useMerchantNames } from "features/merchants/useMerchantNames";
import type { TerminalRecord } from "components/terminal-row/TerminalRow";
import type { PosByTreasury } from "utils/AxiosUtil";

export type TerminalLookupStatus =
  | "idle" // no lookup is running (disabled by the caller, or no auth yet)
  | "loading" // a request is in flight for the current serial
  | "found" // the chain returned a matching terminal
  | "not-found" // the chain answered, definitively, with no such terminal
  | "error"; // the lookup could not be completed (network/server error)

export interface UseTerminalLookupResult {
  status: TerminalLookupStatus;
  terminal: TerminalRecord | null;
  /** Re-run the lookup — used to recover from the `error` state. */
  refetch: () => void;
}

/**
 * Looks a terminal up on-chain by its full hardware serial — the fallback used when it isn't
 * among the recent terminals already loaded. Gated by `enabled` so the request only fires when
 * the caller has decided a lookup is warranted (e.g. the local list came up empty for the typed
 * serial).
 *
 * Unlike the card/merchant/redeem lookups, a miss here isn't a 200 with an empty body — the route
 * throws POS_NOT_FOUND, which axios surfaces as a request error. So the not-found/error split
 * has to read the backend's error code (via parseApiError) rather than just checking whether
 * data came back; any other code still means "couldn't complete the lookup".
 */
export function useTerminalLookup(
  serial: string,
  enabled: boolean,
): UseTerminalLookupResult {
  const { user } = useAuth();
  const treasuryId = useSelector(treasuryIdSelector);
  const networkType = useSelector(networkTypeSelector);
  const normalized = serial.trim();
  const active = !!user && !!treasuryId && enabled && normalized.length > 0;

  const query = useQuery<PosByTreasury>({
    queryKey: ["terminal-lookup", treasuryId, networkType, normalized],
    queryFn: () =>
      issuer_services.lookupTerminal(treasuryId!, normalized, networkType),
    enabled: active,
    retry: false,
    staleTime: 60_000,
  });

  const unnamedMerchantIds = useMemo(
    () =>
      active && query.data && !query.data.unassigned && !query.data.merchantName
        ? [query.data.merchantId]
        : [],
    [active, query.data],
  );
  const { namesMap, pendingIds } = useMerchantNames(unnamedMerchantIds);

  const terminal =
    active && query.data
      ? toTerminalRecord(query.data, namesMap, pendingIds)
      : null;

  const isPosNotFound =
    query.isError && parseApiError(query.error, "").code === "POS_NOT_FOUND";

  let status: TerminalLookupStatus;
  if (!active) {
    status = "idle";
  } else if (query.isFetching) {
    status = "loading";
  } else if (isPosNotFound) {
    status = "not-found";
  } else if (query.isError) {
    status = "error";
  } else if (query.isSuccess) {
    status = terminal ? "found" : "not-found";
  } else {
    // Active but not yet fetching — the tick between enabling and the request
    // actually starting. Treat as loading so we never flash a stale outcome.
    status = "loading";
  }

  return { status, terminal, refetch: query.refetch };
}
